<template>
  <div class="detail-page-tabs-step">
    <div class="intro-text">
      <Typography variant="h-5">Now let's add content to the program detail page</Typography>
      <Typography variant="body-2">
        Add tabs for the participants to get more information. At least one tab is required.
      </Typography>
    </div>

    <div class="preview-row">
      <Button outlined iconLeft="mdi-eye" data-testid="tabs-preview-button" @click="openPreview">
        Preview
      </Button>
    </div>

    <v-divider class="my-6" />

    <div class="tabs-list-header">
      <Typography variant="subtitle-1">Detail Page Tabs</Typography>
      <Button
        text
        iconLeft="mdi-plus"
        data-testid="add-tab-btn"
        :disabled="localTabs.length >= 10"
        @click="addTab"
      >
        Add Tab
      </Button>
    </div>

    <Alert
      v-if="tabsError"
      variant="error"
      :message="tabsError"
      data-testid="tabs-error"
      class="tabs-error"
    />

    <div
      v-if="localTabs.length === 0"
      data-testid="empty-state"
      class="text-center pa-8 grey--text"
    >
      No tabs created yet. Click "Add Tab" to get started.
    </div>

    <draggable
      v-else
      :value="localTabs"
      handle=".tab-handle"
      class="tabs-list"
      @input="handleReorder"
    >
      <div
        v-for="(tab, index) in localTabs"
        :key="tab.tabId || index"
        :data-testid="`tab-card-${index}`"
        class="tab-row"
      >
        <Icon icon="mdi-drag" class="tab-handle cursor-grab" />
        <Typography variant="subtitle-2" class="tab-row__name">{{ tab.tabName }}</Typography>
        <div class="tab-row__actions">
          <Button icon small :data-testid="`edit-tab-${index}`" @click="editTab(tab)">
            <Icon icon="mdi-pencil" color="darkGrey" small />
          </Button>
          <Button
            icon
            small
            :data-testid="`delete-tab-${index}`"
            :disabled="localTabs.length <= 1"
            @click="deleteTab(index)"
          >
            <Icon icon="mdi-delete" color="darkGrey" small />
          </Button>
        </div>
      </div>
    </draggable>

    <TabEditorSideSheet
      v-model="showSideSheet"
      :tab="editingTab"
      @submit="handleTabSubmit"
      @preview="onPreview"
    />

    <ErrorDialogV2
      v-model="showDeleteDialog"
      title="Delete this tab?"
      message="This tab will be permanently removed from the program."
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      :show-cancel-button="true"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />

    <DynamicProgramPreview
      v-model="showPreview"
      show-detail-page
      :program-name="programName"
      :image-placeholder="imageUrl || undefined"
      :date-range-label="dateRangeLabel"
      :tabs="previewTabs"
      :active-tab-key="previewActiveTabKey"
      :metric-rows="previewMetricRows"
      :basis="payoutCadence === 'IMMEDIATE' ? 'PAID' : 'PROJECTED'"
      :is-dollar-budget="isDollarBudget"
      :is-claim-program="isClaimProgram"
    />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import TabEditorSideSheet from "./TabEditorSideSheet.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import Alert from "@/components/generic/Alert.vue";
import DynamicProgramPreview from "./DynamicProgramPreview.vue";
import { toPreviewRows } from "./metricPreview";
import moment from "moment";

export default {
  name: "DetailPageTabsStep",
  components: {
    draggable,
    TabEditorSideSheet,
    ErrorDialogV2,
    Typography,
    Button,
    Icon,
    Alert,
    DynamicProgramPreview,
  },
  props: {
    tabs: { type: Array, default: () => [] },
    programId: { type: Number, default: null },
    programName: { type: String, default: "" },
    imageUrl: { type: String, default: null },
    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
    metricRows: { type: Array, default: () => [] },
    metricProjection: { type: Object, default: null },
    payoutCadence: { type: String, default: null },
    isDollarBudget: { type: Boolean, default: false },
    isClaimProgram: { type: Boolean, default: false },
  },
  data() {
    return {
      showSideSheet: false,
      editingTab: null,
      localTabs: [...this.tabs],
      showDeleteDialog: false,
      tabToDelete: null,
      nextTempId: -1,
      showPreview: false,
      previewActiveTabKey: null,
      previewTabOverride: null,
      tabsError: null,
    };
  },
  computed: {
    dateRangeLabel() {
      const fmt = (d) => (d ? moment(d).format("MMM D, YYYY") : "TBD");
      return `${fmt(this.startDate)} \u2013 ${fmt(this.endDate)}`;
    },
    mappedTabs() {
      return (this.localTabs || []).map((t, i) => ({
        key: t.tabId ?? i,
        label: t.tabName,
        title: t.title,
        bodyContent: t.bodyContent,
      }));
    },
    previewMetricRows() {
      return toPreviewRows(this.metricRows, this.metricProjection);
    },
    previewTabs() {
      if (!this.previewTabOverride) return this.mappedTabs;
      const override = this.previewTabOverride;
      const overrideEntry = {
        label: override.tabName || "New Tab",
        title: override.title,
        bodyContent: override.bodyContent,
      };
      if (this.editingTab && this.editingTab.tabId != null) {
        return this.mappedTabs.map((entry) =>
          entry.key === this.editingTab.tabId ? { ...entry, ...overrideEntry } : entry
        );
      }
      return [...this.mappedTabs, { key: "__preview__", ...overrideEntry }];
    },
  },
  watch: {
    tabs: {
      handler(newTabs) {
        this.localTabs = [...newTabs];
      },
      deep: true,
    },
    showSideSheet(next, prev) {
      if (prev && !next) this.$emit("editor-closed");
    },
    showPreview(newVal, oldVal) {
      if (!newVal && oldVal && this.previewTabOverride) {
        this.showSideSheet = true;
        this.previewTabOverride = null;
      }
    },
  },
  methods: {
    validate() {
      if (this.localTabs.length === 0) {
        this.tabsError = "At least one tab is required";
        return false;
      }
      this.tabsError = null;
      return true;
    },
    openPreview() {
      // Toolbar-level preview: no specific tab to highlight, start at the first one.
      this.previewActiveTabKey = null;
      this.showPreview = true;
    },
    onPreview(formPayload) {
      if (formPayload) {
        this.previewTabOverride = formPayload;
        if (this.editingTab && this.editingTab.tabId != null) {
          this.previewActiveTabKey = this.editingTab.tabId;
        } else {
          this.previewActiveTabKey = "__preview__";
        }
      } else {
        this.previewActiveTabKey = this.editingTab?.tabId ?? null;
      }
      this.showPreview = true;
      this.$nextTick(() => {
        this.showSideSheet = true;
      });
    },
    addTab() {
      this.editingTab = null;
      this.showSideSheet = true;
    },
    editTab(tab) {
      this.editingTab = { ...tab };
      this.showSideSheet = true;
    },
    deleteTab(index) {
      if (this.localTabs.length <= 1) {
        return;
      }
      this.tabToDelete = index;
      this.showDeleteDialog = true;
    },
    confirmDelete() {
      const index = this.tabToDelete;
      const newTabs = this.localTabs
        .filter((_, i) => i !== index)
        .map((tab, i) => ({ ...tab, sortOrder: i }));
      this.$emit("update:tabs", newTabs);
      this.showDeleteDialog = false;
      this.tabToDelete = null;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.tabToDelete = null;
    },
    handleReorder(newList) {
      this.localTabs = newList;
      const reordered = newList.map((tab, i) => ({ ...tab, sortOrder: i }));
      this.$emit("update:tabs", reordered);
    },
    openTabById(id) {
      const tab = this.localTabs.find((t) => t.tabId === id);
      if (tab) this.editTab(tab);
    },
    handleTabSubmit(payload) {
      const newTabs = [...this.localTabs];
      if (payload.tabId != null) {
        const idx = newTabs.findIndex((t) => t.tabId === payload.tabId);
        if (idx !== -1) {
          newTabs[idx] = { ...newTabs[idx], ...payload };
        }
      } else {
        newTabs.push({
          ...payload,
          tabId: this.nextTempId--,
          sortOrder: newTabs.length,
        });
      }
      this.$emit("update:tabs", newTabs);
      this.tabsError = null;
    },
  },
};
</script>

<style scoped>
.intro-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
}

.preview-row {
  margin-bottom: 24px;
}

.tabs-list-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
}

.tabs-error {
  width: 100%;
  margin-bottom: 16px;
}

.tabs-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tab-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background: white;
}

.tab-row__name {
  flex-grow: 1;
}

.tab-row__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
